import React, { useState, useEffect } from 'react';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import { MatchDetails } from '../../../shared/services/matchPointsService';
import { useMatchPointsOperations } from '../../../shared/hooks/useMatchPoints';

interface MatchMappingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (matchIds: string[]) => void;
  tournamentId: string;
  seasonId?: string;
  initialMatchIds?: string[];
}

const MatchMappingModal: React.FC<MatchMappingModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  tournamentId,
  seasonId,
  initialMatchIds = []
}) => {
  const [selectedMatchIds, setSelectedMatchIds] = useState<string[]>(initialMatchIds);
  const [availableMatches, setAvailableMatches] = useState<MatchDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { getMatchesFromMatchPoints } = useMatchPointsOperations();

  // Fetch matches from match points for this tournament and season
  useEffect(() => {
    if (isOpen && tournamentId) {
      setSelectedMatchIds(initialMatchIds); // Set initial selection
      fetchMatchesFromMatchPoints();
    }
  }, [isOpen, tournamentId, seasonId, initialMatchIds]);

  const fetchMatchesFromMatchPoints = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const matches = await getMatchesFromMatchPoints(tournamentId, seasonId);
      setAvailableMatches(matches);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch matches');
    } finally {
      setLoading(false);
    }
  };

  const handleMatchToggle = (matchId: string) => {
    setSelectedMatchIds(prev => {
      if (prev.includes(matchId)) {
        return prev.filter(id => id !== matchId);
      } else {
        return [...prev, matchId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedMatchIds.length === availableMatches.length) {
      setSelectedMatchIds([]);
    } else {
      setSelectedMatchIds(availableMatches.map(match => match.id));
    }
  };

  const handleSave = () => {
    onSuccess(selectedMatchIds);
    onClose();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Map Matches to Configuration">
      <div className="p-4">
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">
            Select matches from team standings to map to this tournament configuration.
          </p>
          <p className="text-xs text-gray-500">
            Only matches present in team standings for tournament {tournamentId} 
            {seasonId && ` and season ${seasonId}`} are shown.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-8">
            <div className="text-gray-400 text-4xl mb-4">⏳</div>
            <p className="text-gray-500">Loading matches...</p>
          </div>
        ) : (
          <>
            {/* Select All Toggle */}
            <div className="mb-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={selectedMatchIds.length === availableMatches.length && availableMatches.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Select All ({availableMatches.length} matches)
                </span>
              </label>
            </div>

            {/* Matches List */}
            <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
              {availableMatches.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <div className="text-gray-400 text-3xl mb-3">⚽</div>
                  <p className="text-sm">No matches found in team standings</p>
                  <p className="text-xs mt-1 text-gray-400">Matches must exist in team standings to be mapped</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {availableMatches.map((match) => (
                    <div key={match.id} className="p-3 hover:bg-gray-50 transition-colors">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedMatchIds.includes(match.id)}
                          onChange={() => handleMatchToggle(match.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-grow min-w-0">
                          <div className="flex items-center justify-between">
                            <div className="min-w-0 flex-1">
                              {match.status === 'scheduled' ? (
                                // For scheduled matches: Home Team vs Away Team
                                <p className="text-sm font-medium text-gray-900">
                                  {match.home_team_name} <span className="text-gray-400">vs</span> {match.away_team_name}
                                </p>
                              ) : (
                                // For live/finished matches: Home Team [Score] Away Team
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm text-gray-700">{match.home_team_name}</span>
                                  <span className="text-base font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-md">
                                    {match.home_score} - {match.away_score}
                                  </span>
                                  <span className="text-sm text-gray-700">{match.away_team_name}</span>
                                </div>
                              )}
                              {/* Show penalty scores if they exist and are relevant (not for scheduled matches) */}
                              {match.home_penalty_score !== undefined && match.away_penalty_score !== undefined &&
                               (match.home_penalty_score > 0 || match.away_penalty_score > 0) && match.status !== 'scheduled' &&
                                <p className="text-xs text-gray-500 mt-1">penalties ({match.home_penalty_score}) - ({match.away_penalty_score})</p>
                              }
                            </div>
                            <div className="text-right ml-2 flex-shrink-0">
                              <p className="text-xs text-gray-500">
                                {formatDate(match.match_date)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Selected Count */}
            <div className="mt-4 text-sm text-gray-600">
              {selectedMatchIds.length} of {availableMatches.length} matches selected
            </div>
          </>
        )}

        {/* Action Buttons */}
        <div className="mt-6 flex justify-end space-x-3">
          <Button
            onClick={onClose}
            variant="secondary"
            size="md"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="primary"
            size="md"
            disabled={loading}
          >
            Save Mapping
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default MatchMappingModal;
